import { Box, Flex, Icon, Text, useToast } from '@chakra-ui/react';
import { showToast } from '@diamond/shared/ui';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import { useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

interface DropzoneOcrProps {
  disabled?: boolean;
  maxFiles: number;
  maxSizeMb?: number;
  onFilesSelected: (files: File[]) => void;
}

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

function getRejectionMessage(
  rejections: FileRejection[],
  maxSizeMb: number,
  maxFiles: number
) {
  const code = rejections[0]?.errors[0]?.code;
  if (code === 'file-too-large') {
    return `Ukuran file maksimal ${maxSizeMb} MB`;
  }
  if (code === 'file-invalid-type') {
    return 'Hanya menerima file PDF, PNG, JPG';
  }
  if (code === 'too-many-files') {
    return `Maksimal ${maxFiles} file yang dapat diunggah`;
  }
  return rejections[0]?.errors[0]?.message ?? 'File tidak dapat diunggah';
}

export const DropzoneOcr = ({
  disabled = false,
  maxFiles,
  maxSizeMb = 5,
  onFilesSelected,
}: DropzoneOcrProps) => {
  const toast = useToast();

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      showToast(
        toast,
        'error',
        getRejectionMessage(rejections, maxSizeMb, maxFiles)
      );
    },
    [maxFiles, maxSizeMb, toast]
  );

  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_TYPES,
    disabled,
    maxFiles,
    maxSize: maxSizeMb * 1000000,
    multiple: true,
    noClick: true,
    onDropAccepted: onFilesSelected,
    onDropRejected: handleDropRejected,
  });

  const handleOpen = useCallback(() => {
    if (disabled) return;
    open();
  }, [disabled, open]);

  return (
    <Box>
      <Flex
        {...getRootProps()}
        align="center"
        bg={disabled ? '#F5F5F5' : '#F3F4F5'}
        border="2px dashed"
        borderColor={isDragActive ? '#0000A3' : '#D9D9D9'}
        borderRadius="8px"
        color={disabled ? '#A0A0A0' : '#131415'}
        cursor={disabled ? 'not-allowed' : 'pointer'}
        gap="12px"
        h="90px"
        justify="center"
        w="330px"
        px={6}
        py={6}
        transition="border-color 0.2s ease, background 0.2s ease"
        data-testid="dropzone-ocr"
        onClick={handleOpen}
      >
        <input {...getInputProps()} data-testid="dropzone-ocr-input" />
        <Icon as={CloudUploadOutlined} boxSize="32px" color="blue" />
        <Text fontSize="14px" lineHeight="20px">
          Tarik & lepas dokumen disini atau{' '}
          <Box as="span" color="blue" fontWeight={600}>
            Unggah
          </Box>
        </Text>
      </Flex>
      <Text color="#999999" fontSize="12px" lineHeight="15px" mt={2} w="330px">
        Hanya menerima format PDF, PNG, JPG dengan ukuran maksimal {maxSizeMb}{' '}
        MB
      </Text>
    </Box>
  );
};
